import type { ComponentType, FC, ReactElement } from 'react'

import {
  DataPoint,
  DataPoints,
  ExpandableDescription,
  ExternalLink,
  Icon,
  StyledText,
  Tooltip,
} from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { isNil } from '@skytech/utils'
import ContentCopyIcon from 'mdi-react/ContentCopyIcon'
import OpenInNewIcon from 'mdi-react/OpenInNewIcon'

import type { Product } from '@/components/ecom-policy/types'
import { FormattedNumberWithCurrency } from '@/components/FormattedNumberWithCurrency'
import { useTranslation } from '@/components/i18n/hooks'
import { Translation } from '@/components/i18n/Translation'

export type ProductInfoProps = {
  product: Product
  isSingle: boolean
  selectBtn?: ReactElement
  onClose?: () => void
}

export const ProductInfo: FC<ProductInfoProps> = ({
  product,
  isSingle = false,
  selectBtn,
  onClose,
}) => {
  const translate = useTranslation()
  const {
    name,
    alias,
    aliasDescription,
    sku,
    code,
    price,
    description,
    webUrl,
    manufacturerName,
    manufacturerProductNo,
  } = product

  return (
    <div className="flex flex-col gap-4">
      <DataPoints className="!m-0 !p-0">
        {/* Article name */}
        <DataPoint
          className="!mx-0 !flex-[100%] max-sm:!my-0 max-sm:!mb-2"
          value={
            <StyledText
              size="xxl"
              fontWeight="bold"
              className="!leading-none max-md:!text-xl max-sm:!text-lg"
            >
              {alias || name}
            </StyledText>
          }
        />

        {/* Product vendor unique ID / Article number */}
        {(!isNil(product.sku) || !isNil(product.code)) && (
          <DataPoint
            className="!mx-0 max-sm:!my-0 max-sm:!mb-2"
            header={sku ? <Translation id="Vendor unique ID" /> : <Translation id="Article No." />}
            value={
              <span className="flex items-center !leading-none max-md:text-base max-sm:text-sm">
                {sku || code}
                <Button
                  size="2xs"
                  variant="tertiary"
                  Icon={<ContentCopyIcon />}
                  className="m-0 mb-1 ml-1 p-0"
                  data-tooltip-id="copy"
                  data-tooltip-content={translate('Copy')}
                  data-event="click focus"
                  onClick={e => {
                    e.stopPropagation()
                    if (sku == null && code == null) {
                      return
                    }
                    const copyText = sku || code || ''
                    window.navigator.clipboard.writeText(copyText)
                  }}
                >
                  <Tooltip id="copy" size="base" />
                </Button>
              </span>
            }
          />
        )}

        {/* Product manufacturer name */}
        <DataPoint
          className="!mx-0 max-sm:!my-0 max-sm:!mb-2"
          header={<Translation id="Manufacturer" />}
          value={
            <span className="!leading-none max-md:text-base max-sm:text-sm">
              {manufacturerName ? manufacturerName : '-'}
            </span>
          }
        />

        {/* Product manufacturer number */}
        <DataPoint
          className="!mx-0 max-sm:!my-0 max-sm:!mb-2"
          header={<Translation id="Manufacturer number" />}
          value={
            <span className="flex items-center !leading-none max-md:text-base max-sm:text-sm">
              {manufacturerProductNo ? manufacturerProductNo : '-'}
              <Button
                size="2xs"
                variant="tertiary"
                Icon={<ContentCopyIcon />}
                className="m-0 mb-1 ml-1 p-0"
                data-tooltip-id="copy"
                data-tooltip-content={translate('Copy')}
                data-event="click focus"
                onClick={e => {
                  if (!manufacturerProductNo) {
                    return
                  }
                  e.stopPropagation()
                  window.navigator.clipboard.writeText(manufacturerProductNo)
                }}
              >
                <Tooltip id="copy" size="base" />
              </Button>
            </span>
          }
        />

        {/* Product URL */}
        <DataPoint
          className="!mx-0 !flex-[100%] max-sm:!my-0 max-sm:!mb-2"
          header={<Translation id="Product page" />}
          value={
            webUrl ? (
              <ExternalLink
                isWhiteLabeled
                className="text-sm !leading-none hover:cursor-pointer hover:underline sm:text-base"
                hasUnderline={false}
                href={webUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {webUrl}
                <Icon className="ml-1" size="small">
                  <OpenInNewIcon />
                </Icon>
              </ExternalLink>
            ) : (
              '-'
            )
          }
        />

        {/* Product description */}
        <DataPoint
          className="!mx-0 !flex-[100%] max-sm:!my-0 max-sm:!mb-2"
          header={<Translation id="Description" className="capitalize" />}
          value={
            aliasDescription || description ? (
              <ExpandableDescription
                description={aliasDescription || description || ''}
                showFullText={false}
                TranslationComponent={Translation as ComponentType}
              />
            ) : (
              '-'
            )
          }
        />
        {/* Product price */}
        <DataPoint
          className="!mx-0 !flex-[100%] max-sm:!my-0 max-sm:!mb-2"
          header={<Translation id="Price" />}
          value={<FormattedNumberWithCurrency amount={price} />}
        />
      </DataPoints>

      <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
        {Boolean(selectBtn) && selectBtn}

        {isSingle && onClose && (
          <Button onClick={onClose} className="m-0 min-w-full sm:min-w-32">
            <Translation id="Close" />
          </Button>
        )}
      </div>
    </div>
  )
}
